import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatCustomDate } from "../../utils/formatters";
import SupportCategoryModal from "./modal";

export default function SupportCategories() {
  const [refresh, setRefresh] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const columns = [
    { field: "id", headerName: "ID", width: 70, type: "number" },
    { field: "name", headerName: "Name", width: 500 },
    {
      field: "created_at",
      headerName: "Date Created",
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.created_at),
      width: 150,
      type: "date",
    },
    {
      field: "",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("support-category-update") && (
            <IconButton
              color="info"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenEdit(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {checkPermission("support-category-delete") && (
            <IconButton
              color="error"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenConfirm(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleModalClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenConfirm(false);
    setCurrentRow(null);
  };

  const handleCategoryAdd = async (formData) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/support/categories`,
        formData,
      );

      if (response.status === 201) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully created a new category.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to create category. Please try again.", { icon: "error" });
    }
  };

  const handleCategoryEdit = async (formData, id) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_URL}/support/categories/${id}`,
        formData,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully updated category.", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update category. Please try again.", {
        icon: "error",
      });
    }
  };

  const handleCategoryDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/support/categories/${currentRow.id}`,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully deleted category", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete category. Please try again.", { icon: "error" });
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Support Categories</Typography>
          {checkPermission("support-category-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add new
            </Button>
          )}
        </Box>

        <CustomTable
          columns={columns}
          fetchDataUrl="support/categories-paginated"
          refresh={refresh}
        />
      </MainLayout>

      <SupportCategoryModal
        open={openAdd}
        onClose={handleModalClose}
        onSubmit={handleCategoryAdd}
      />

      {currentRow && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleCategoryDelete}
          title="Are you sure you want to delete this category?"
          message={
            <span>
              <strong>{currentRow.name}</strong> category will be deleted.
              Please note that this action is not reversible.
            </span>
          }
        />
      )}

      {currentRow && openEdit && (
        <SupportCategoryModal
          open={openEdit}
          onClose={handleModalClose}
          onSubmit={handleCategoryEdit}
          category={currentRow}
        />
      )}
    </>
  );
}
